import { useDesignMode } from "@/hooks/useDesignMode";
import { Button } from "@/components/ui/button";
import { LAYER, getLayerLabel } from "./layerConstants";
import {
  Eye,
  EyeOff,
  Lock,
  Unlock,
  ChevronUp,
  ChevronDown,
  Layers,
  X,
  GripVertical,
} from "lucide-react";
import { useState } from "react";

const LayersPanel = () => {
  const {
    isDesignMode,
    elements,
    selectedElementId,
    selectElement,
    updateElement,
  } = useDesignMode();

  const [isOpen, setIsOpen] = useState(false);

  if (!isDesignMode) return null;

  const sortedElements = [...elements]
    .filter((el) => el.type === "overlay")
    .sort((a, b) => b.z_index - a.z_index);

  if (!isOpen) {
    return (
      <button
        data-editor-ui
        data-design-panel
        onClick={(e) => { e.stopPropagation(); setIsOpen(true); }}
        className="fixed left-4 top-20 z-[9998] bg-card border border-border rounded-xl shadow-lg p-2.5 hover:bg-muted transition-colors"
        title="Panneau des calques"
      >
        <Layers className="w-5 h-5 text-primary" />
      </button>
    );
  }

  return (
    <div
      data-editor-ui
      data-design-panel
      className="fixed left-4 top-20 z-[9998] w-64 bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-muted/50 border-b border-border">
        <span className="text-sm font-semibold flex items-center gap-1.5">
          <Layers className="w-4 h-4 text-primary" />
          Calques
        </span>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setIsOpen(false)}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Layer groups */}
      <div className="max-h-[50vh] overflow-y-auto">
        {/* System layers (always shown as info) */}
        <div className="px-3 py-1.5 bg-muted/30 border-b border-border">
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
            🔒 Navigation (z: {LAYER.NAVBAR.min}+)
          </span>
        </div>
        <div className="px-3 py-1.5 text-xs text-muted-foreground flex items-center gap-2">
          <Lock className="w-3 h-3" />
          <span>Navbar — toujours au-dessus</span>
        </div>

        {/* Overlay elements */}
        <div className="px-3 py-1.5 bg-muted/30 border-b border-t border-border">
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
            📐 Éléments ({sortedElements.length})
          </span>
        </div>

        {sortedElements.length === 0 && (
          <div className="px-3 py-4 text-xs text-muted-foreground text-center">
            Aucun élément. Ajoutez une image.
          </div>
        )}

        {sortedElements.map((el) => {
          const isSelected = selectedElementId === el.id;
          const layerLabel = getLayerLabel(el.z_index);
          
          return (
            <div
              key={el.id}
              className={`flex items-center gap-1 px-2 py-1.5 border-b border-border/50 cursor-pointer transition-colors ${
                isSelected ? "bg-primary/10" : "hover:bg-muted/50"
              }`}
              onClick={() => selectElement(el.id)}
            >
              <GripVertical className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />
              
              {/* Thumbnail */}
              {el.image_url && (
                <div className="w-7 h-7 rounded border bg-muted flex-shrink-0 overflow-hidden">
                  <img src={el.image_url} alt="" className="w-full h-full object-contain" />
                </div>
              )}
              
              {/* Name + layer info */}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">
                  {el.label || "Image"}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {layerLabel} · z:{el.z_index}
                </div>
              </div>

              {/* Quick actions */}
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <button
                  className="p-1 rounded hover:bg-muted"
                  onClick={(e) => { e.stopPropagation(); updateElement(el.id, { isLocked: !el.isLocked }); }}
                  title={el.isLocked ? "Déverrouiller" : "Verrouiller"}
                >
                  {el.isLocked ? <Lock className="w-3 h-3 text-amber-500" /> : <Unlock className="w-3 h-3 text-muted-foreground" />}
                </button>
                <button
                  className="p-1 rounded hover:bg-muted"
                  onClick={(e) => { e.stopPropagation(); updateElement(el.id, { isHidden: !el.isHidden }); }}
                  title={el.isHidden ? "Afficher" : "Masquer"}
                >
                  {el.isHidden ? <EyeOff className="w-3 h-3 text-muted-foreground" /> : <Eye className="w-3 h-3 text-muted-foreground" />}
                </button>
                <button
                  className="p-1 rounded hover:bg-muted"
                  onClick={(e) => { e.stopPropagation(); moveElement(el.id, "up"); }}
                  title="Monter"
                >
                  <ChevronUp className="w-3 h-3 text-muted-foreground" />
                </button>
                <button
                  className="p-1 rounded hover:bg-muted"
                  onClick={(e) => { e.stopPropagation(); moveElement(el.id, "down"); }}
                  title="Descendre"
                >
                  <ChevronDown className="w-3 h-3 text-muted-foreground" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  function moveElement(id: string, direction: "up" | "down") {
    const idx = sortedElements.findIndex((e) => e.id === id);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sortedElements.length) return;
    
    const currentZ = sortedElements[idx].z_index;
    const swapZ = sortedElements[swapIdx].z_index;
    
    updateElement(sortedElements[idx].id, { z_index: swapZ });
    updateElement(sortedElements[swapIdx].id, { z_index: currentZ });
  }
};

export default LayersPanel;
